import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslate, useNotification } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Form } from "@shared/components";
import { AgreementsAutocomplete } from "@shared/components/AutocompleteFields";

import { financialQueryKey, useRebuildFinancialMutation } from "../services";
import { toRebuildPayload } from "../params";
import { FinancialFilterValues } from "../types";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function yearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, index) => current - 4 + index);
}

const EMPTY_VALUES: FinancialFilterValues = {
  integration: "",
  agreement_id: null,
  cpf: "",
  user_id: "",
  product_id: null,
  plan_slug_integration: "",
  level_id: null,
  year: "",
  month: "",
  media_type: "",
  status: "",
  tipo: "",
  situacao: "",
  modelo: "",
};

interface RebuildDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RebuildDialog({ open, onClose }: RebuildDialogProps) {
  const translate = useTranslate();
  const { open: openNotification } = useNotification();
  const queryClient = useQueryClient();
  const { mutate, isPending, reset } = useRebuildFinancialMutation();
  const methods = useForm<FinancialFilterValues>({ defaultValues: EMPTY_VALUES });

  useEffect(() => {
    if (!open) {
      methods.reset(EMPTY_VALUES);
      reset();
    }
  }, [open, methods, reset]);

  const handleSubmit = (values: FinancialFilterValues) => {
    if (values.month && !values.year) {
      methods.setError("month", { message: translate("financial_admin.messages.month_requires_year") });
      return;
    }

    mutate(toRebuildPayload(values), {
      onSuccess: () => {
        openNotification?.({
          type: "success",
          message: translate("financial_admin.messages.rebuild_started"),
        });
        queryClient.invalidateQueries({ queryKey: [financialQueryKey] });
        onClose();
      },
      onError: (error: Error) => {
        const data = (error as AxiosError<{ detail?: string }>)?.response?.data;
        openNotification?.({
          type: "error",
          message: data?.detail || translate("financial_admin.messages.rebuild_failed"),
        });
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{translate("financial_admin.rebuild.title")}</DialogTitle>
      <Form methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate("financial_admin.rebuild.hint")}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <AgreementsAutocomplete
                name="agreement_id"
                label={translate("financial_admin.filters.agreement")}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Form.Select
                name="year"
                label={translate("financial_admin.filters.year")}
                options={[
                  { value: "", label: translate("financial_admin.filters.current_period") },
                  ...yearOptions().map((year) => ({ value: String(year), label: String(year) })),
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Form.Select
                name="month"
                label={translate("financial_admin.filters.month")}
                options={[
                  { value: "", label: translate("financial_admin.filters.until_current_month") },
                  ...MONTHS.map((month) => ({
                    value: String(month),
                    label: translate(`financial_admin.months.${month}`),
                  })),
                ]}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isPending}>
            {translate("financial_admin.actions.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isPending}>
            {translate("financial_admin.actions.rebuild")}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
}
